import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { GradeBadge, StatusBadge } from "@/components/Badges";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
} from "recharts";
import {
  TrendingUp, AlertTriangle, DollarSign, Briefcase, Users,
  AlarmClock, FileText, Brain, TrendingDown, Layers,
} from "lucide-react";
import { Link } from "react-router-dom";
import { ClassificationBadge } from "@/components/Badges";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ExportMenu from "@/components/ExportMenu";

const GRADE_COLORS = {
  Alpha: "#1E8449", Beta: "#2E86C1", Gama: "#F39C12",
  A: "#1E8449", B: "#2E86C1", C: "#F39C12", D: "#C0392B",
  ungraded: "#94A3B8",
};

function KpiCard({ label, value, icon: Icon, tone = "default", testid }) {
  const tones = {
    default: "text-slate-800",
    success: "text-[#1E8449]",
    warning: "text-[#F39C12]",
    danger: "text-[#C0392B]",
    accent: "text-[#2E86C1]",
  };
  return (
    <div className="mptg-card" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="mptg-overline">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
      </div>
      <div className={`font-heading text-2xl font-bold tracking-tight mt-2 ${tones[tone]}`}>
        {value}
      </div>
    </div>
  );
}

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })
    .format(n || 0);

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientId, setClientId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [clients, setClients] = useState([]);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    // one-shot: fetch clients + branches for filter dropdowns
    Promise.all([
      api.get("/clients", { params: { page_size: 500 } }).then((r) => r.data.items),
      api.get("/branches").then((r) => r.data),
    ]).then(([cs, bs]) => {
      setClients(cs);
      setBranches(bs);
    });
    // mark adverse-events badge as seen — dashboard is the entry point
    // for catching up on portfolio events.
    api.post("/dashboard/mark-adverse-seen").then(() => {
      window.dispatchEvent(new Event("mptg:adverse-seen"));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/dashboard/portfolio", {
          params: {
            client_id: clientId || undefined,
            branch_id: branchId || undefined,
          },
        });
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [clientId, branchId]);

  if (loading && !data) return <div className="p-8 text-slate-500" data-testid="dashboard-loading">Loading dashboard…</div>;
  if (!data) return <div className="p-8 text-slate-500">No data</div>;

  const gradeData = Object.entries(data.grade_distribution)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: k, value: v, fill: GRADE_COLORS[k] }));

  const branchData = data.branch_summary || [];

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="portfolio-dashboard">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <div className="mptg-overline">Portfolio · {ROLE_LABELS[user?.role]}</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">
            Welcome back, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-slate-500 mt-1">Snapshot of your portfolio health as of today.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="dashboard-exports">
          <ExportMenu endpoint="/dashboard/export" params={{ kind: "clients", client_id: clientId, branch_id: branchId }} filename="clients" label="Clients" testid="export-clients" />
          <ExportMenu endpoint="/dashboard/export" params={{ kind: "branches", branch_id: branchId }} filename="branches" label="Branches" testid="export-branches" />
          <ExportMenu endpoint="/dashboard/export" params={{ kind: "collections", client_id: clientId }} filename="collections" label="Collections" testid="export-collections" />
          <ExportMenu endpoint="/dashboard/export" params={{ kind: "campaigns", client_id: clientId, branch_id: branchId }} filename="campaigns" label="Campaigns" testid="export-campaigns" />
        </div>
      </div>

      {/* Filters */}
      <div className="mptg-card flex flex-wrap items-center gap-3" data-testid="dashboard-filters">
        <span className="mptg-overline">Filter</span>
        <Select value={clientId || "all"} onValueChange={(v) => setClientId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-64" data-testid="dash-client-filter">
            <SelectValue placeholder="All clients" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={branchId || "all"} onValueChange={(v) => setBranchId(v === "all" ? "" : v)}>
          <SelectTrigger className="w-64" data-testid="dash-branch-filter">
            <SelectValue placeholder="All branches" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All branches</SelectItem>
            {branches.map((b) => (
              <SelectItem key={b.id} value={b.id}>{b.code} · {b.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(clientId || branchId) && (
          <button
            className="text-xs text-[#2E86C1] hover:underline"
            onClick={() => { setClientId(""); setBranchId(""); }}
            data-testid="dash-clear-filters"
          >
            Clear
          </button>
        )}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard label="Clients" value={data.client_count} icon={Users} testid="kpi-clients" />
        <KpiCard label="Campaigns" value={data.campaign_count} icon={Briefcase} testid="kpi-campaigns" />
        <KpiCard label="Revenue YTD" value={inr(data.total_revenue_ytd)} icon={DollarSign} tone="accent" testid="kpi-revenue" />
        <KpiCard label="Outstanding" value={inr(data.total_outstanding)} icon={AlertTriangle} tone="warning" testid="kpi-outstanding" />
        <KpiCard label="KPI Red (MTD)" value={data.kpi_red_count} icon={TrendingUp} tone={data.kpi_red_count ? "danger" : "success"} testid="kpi-red-count" />
        <KpiCard label="At-risk clients" value={data.at_risk_clients.length} icon={AlarmClock} tone={data.at_risk_clients.length ? "warning" : "success"} testid="kpi-at-risk" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Grade distribution */}
        <div className="mptg-card lg:col-span-4">
          <div className="mptg-overline">Client grade distribution</div>
          <div className="h-52 mt-3">
            {gradeData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">No data</div>
            ) : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={gradeData} dataKey="value" nameKey="name" innerRadius={44} outerRadius={76} paddingAngle={2}>
                    {gradeData.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#1B2A4A", border: 0, color: "#fff", fontSize: 12 }} />
                  <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Branch summary */}
        <div className="mptg-card lg:col-span-8">
          <div className="mptg-overline">Branch performance — campaigns / clients / KPI RAG (MTD)</div>
          <div className="overflow-x-auto mt-3">
            <table className="w-full mptg-table">
              <thead><tr><th>Branch</th><th className="text-right">Campaigns</th><th className="text-right">Clients</th><th>KPI RAG (MTD)</th></tr></thead>
              <tbody>
                {branchData.map((b) => (
                  <tr key={b.branch_id || b.branch}>
                    <td className="font-semibold">{b.branch}</td>
                    <td className="text-right tabular-nums">{b.campaign_count}</td>
                    <td className="text-right tabular-nums">{b.client_count}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className="badge-green rounded px-1.5 py-0.5 text-[11px] font-semibold">● {b.rag?.green ?? 0}</span>
                        <span className="badge-amber rounded px-1.5 py-0.5 text-[11px] font-semibold">● {b.rag?.amber ?? 0}</span>
                        <span className="badge-red rounded px-1.5 py-0.5 text-[11px] font-semibold">● {b.rag?.red ?? 0}</span>
                        <span className="badge-slate rounded px-1.5 py-0.5 text-[11px] font-semibold">● {b.rag?.no_data ?? 0}</span>
                      </div>
                    </td>
                  </tr>
                ))}
                {branchData.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-400">No branches in scope.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* At-risk clients */}
        <div className="mptg-card" data-testid="at-risk-list">
          <div className="flex items-center justify-between">
            <div className="mptg-overline">At-risk clients (C / D)</div>
            <Link to="/clients?grade=C" className="text-xs text-[#2E86C1] hover:underline">All clients →</Link>
          </div>
          <div className="mt-3">
            {data.at_risk_clients.length === 0 ? (
              <div className="py-6 text-center text-slate-400 text-sm">No at-risk clients.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.at_risk_clients.slice(0, 8).map((c) => (
                  <li key={c.id} className="py-2.5 flex items-center justify-between">
                    <Link to={`/clients/${c.id}`} className="text-sm font-medium text-slate-800 hover:text-[#2E86C1]">
                      {c.name}
                    </Link>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{c.industry}</span>
                      <GradeBadge grade={c.latest_grade} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Adverse intel last 7 days */}
        <div className="mptg-card" data-testid="adverse-intel-list">
          <div className="flex items-center justify-between">
            <div className="mptg-overline">Adverse intelligence · last 7 days</div>
            <Link to="/intelligence" className="text-xs text-[#2E86C1] hover:underline inline-flex items-center gap-1">
              <Brain className="w-3 h-3" /> Feed →
            </Link>
          </div>
          <div className="mt-3">
            {(data.adverse_intel_7d || []).length === 0 ? (
              <div className="py-6 text-center text-slate-400 text-sm">No adverse signals.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.adverse_intel_7d.slice(0, 6).map((r) => (
                  <li key={r.id} className="py-2.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <ClassificationBadge classification={r.classification} />
                      <Link to={`/clients/${r.client_id}`} className="text-xs font-semibold text-[#2E86C1] hover:underline">
                        {r.client_name}
                      </Link>
                      <span className="text-[11px] text-slate-400">{(r.created_at || "").slice(0, 10)}</span>
                    </div>
                    <div className="text-sm mt-1 text-slate-800 line-clamp-2">{r.headline}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Contracts expiring */}
        <div className="mptg-card lg:col-span-2" data-testid="expiring-contracts">
          <div className="flex items-center justify-between">
            <div className="mptg-overline">Contracts expiring in 90 days</div>
            <FileText className="w-4 h-4 text-slate-400" />
          </div>
          <div className="mt-3">
            {data.contracts_expiring_90d.length === 0 ? (
              <div className="py-6 text-center text-slate-400 text-sm">No contracts expiring soon.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {data.contracts_expiring_90d.slice(0, 8).map((row, i) => (
                  <li key={i} className="py-2.5 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{row.client?.name}</div>
                      <div className="text-xs text-slate-500">Ends {row.contract.end_date}</div>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-md ${
                      row.days_remaining <= 30 ? "badge-red" : row.days_remaining <= 60 ? "badge-amber" : "badge-blue"
                    }`}>
                      {row.days_remaining} days
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <RedKpiHighlights />

      <PortfolioPivot />
    </div>
  );
}


function trendDot(status) {
  const map = {
    red: "bg-[#C0392B]",
    amber: "bg-[#F39C12]",
    green: "bg-[#1E8449]",
    no_data: "bg-slate-200",
  };
  return <span className={`inline-block w-2 h-2 rounded-full ${map[status] || "bg-slate-300"}`} />;
}

function RedKpiHighlights() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ rows: [], month: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/dashboard/red-kpis", { params: { limit: 20 } }).then((r) => r.data),
      api.get("/dashboard/timeline-summary").then((r) => r.data).catch(() => ({ rows: [], month: "" })),
    ])
      .then(([rk, ts]) => {
        if (cancelled) return;
        setRows(rk.rows || []);
        setSummary(ts);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const summaryByClient = summary.rows.reduce((m, r) => { m[r.client_id] = r; return m; }, {});
  const topSummary = summary.rows.slice(0, 6);

  return (
    <div className="mptg-card" data-testid="red-kpi-highlights">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded bg-rose-100 text-[#C0392B] flex items-center justify-center">
            <TrendingDown className="w-4 h-4" />
          </div>
          <div>
            <div className="mptg-overline">Latest Red KPIs · this month</div>
            <div className="text-xs text-slate-500">Worst-performing KPIs across your portfolio (sorted by variance)</div>
          </div>
        </div>
        <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-rose-100 text-[#C0392B]" data-testid="red-kpi-count">
          {loading ? "…" : rows.length}
        </span>
      </div>

      {/* Per-client event-count strip — pulled from /dashboard/timeline-summary */}
      {topSummary.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="timeline-summary-strip">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 self-center">
            Activity {summary.month && `· ${summary.month}`} →
          </div>
          {topSummary.map((s) => (
            <Link
              key={s.client_id}
              to={`/clients/${s.client_id}?tab=timeline`}
              className="text-xs flex items-center gap-1.5 border border-slate-200 rounded-full pl-2 pr-1 py-0.5 hover:border-[#2E86C1] hover:bg-[#EBF5FB] transition-colors"
              data-testid={`timeline-strip-${s.client_id}`}
              title={`${s.client_name}: +${s.positive} positive · −${s.adverse} adverse · ${s.neutral} neutral`}
            >
              <span className="font-semibold text-slate-700 max-w-[140px] truncate">{s.client_name}</span>
              {s.positive > 0 && (
                <span className="rounded-full px-1.5 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-bold">
                  +{s.positive}
                </span>
              )}
              {s.adverse > 0 && (
                <span className="rounded-full px-1.5 py-0.5 bg-rose-100 text-rose-800 text-[10px] font-bold">
                  −{s.adverse}
                </span>
              )}
              {s.positive === 0 && s.adverse === 0 && (
                <span className="rounded-full px-1.5 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-bold">
                  {s.neutral}
                </span>
              )}
            </Link>
          ))}
          {summary.rows.length > 6 && (
            <span className="text-xs text-slate-400 self-center">+{summary.rows.length - 6} more</span>
          )}
        </div>
      )}

      <div className="mt-3 overflow-x-auto">
        {loading ? (
          <div className="py-6 text-center text-slate-400 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-6 text-center text-slate-400 text-sm">No red KPIs in scope this month — keep it up!</div>
        ) : (
          <table className="w-full mptg-table text-sm">
            <thead>
              <tr>
                <th>KPI</th>
                <th>Client · Activity</th>
                <th>Campaign</th>
                <th>Branch</th>
                <th className="text-right">Actual</th>
                <th className="text-right">Benchmark</th>
                <th className="text-right">Variance %</th>
                <th>3-month trend</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 10).map((r) => {
                const s = summaryByClient[r.client_id];
                return (
                <tr key={r.kpi_id} data-testid={`red-kpi-row-${r.kpi_id}`}>
                  <td>
                    <div className="font-semibold text-slate-800">{r.kpi_name}</div>
                    <div className="text-[11px] text-slate-400 uppercase tracking-wider">
                      {r.unit} · {r.direction === "higher_is_better" ? "↑ better" : "↓ better"}
                    </div>
                  </td>
                  <td>
                    <Link to={`/clients/${r.client_id}`} className="text-[#2E86C1] hover:underline">
                      {r.client_name}
                    </Link>
                    {s && (s.positive + s.adverse > 0) && (
                      <div className="mt-0.5 flex items-center gap-1 text-[10px]">
                        {s.positive > 0 && (
                          <span className="rounded px-1 py-0 bg-emerald-100 text-emerald-800 font-bold">
                            +{s.positive}
                          </span>
                        )}
                        {s.adverse > 0 && (
                          <span className="rounded px-1 py-0 bg-rose-100 text-rose-800 font-bold">
                            −{s.adverse}
                          </span>
                        )}
                        <span className="text-slate-400">events MTD</span>
                      </div>
                    )}
                  </td>
                  <td>
                    <Link to={`/campaigns/${r.campaign_id}`} className="text-slate-700 hover:text-[#2E86C1] hover:underline text-xs">
                      {r.campaign_name}
                    </Link>
                  </td>
                  <td className="text-xs text-slate-500">{r.branch}</td>
                  <td className="text-right tabular-nums font-semibold">{r.actual_value ?? "—"}</td>
                  <td className="text-right tabular-nums text-slate-500">{r.benchmark ?? "—"}</td>
                  <td className="text-right tabular-nums font-semibold text-[#C0392B]">
                    {r.variance != null ? `${r.variance.toFixed(1)}%` : "—"}
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      {(r.trend || []).map((t, i) => (
                        <span key={i} className="inline-flex items-center gap-1" title={`${t.month}: ${t.status}`}>
                          {trendDot(t.status)}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const inrCompact = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR", maximumFractionDigits: 0,
    notation: Math.abs(n || 0) >= 1e7 ? "compact" : "standard",
  }).format(n || 0);

function PortfolioPivot() {
  const [dimension, setDimension] = useState("branch");
  const [data, setData] = useState({ rows: [], dimension });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/dashboard/portfolio-pivot", { params: { dimension } })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dimension]);

  return (
    <div className="mptg-card" data-testid="portfolio-pivot">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded bg-[#EBF5FB] text-[#2E86C1] flex items-center justify-center">
            <Layers className="w-4 h-4" />
          </div>
          <div>
            <div className="mptg-overline">Portfolio · dimensions × metrics</div>
            <div className="text-xs text-slate-500">
              The full set of accounts you manage, sliced by{" "}
              <span className="font-semibold text-slate-700">{dimension}</span>
            </div>
          </div>
        </div>
        <Tabs value={dimension} onValueChange={setDimension}>
          <TabsList data-testid="pivot-dim-tabs">
            <TabsTrigger value="branch" data-testid="pivot-dim-branch">By Branch</TabsTrigger>
            <TabsTrigger value="client" data-testid="pivot-dim-client">By Client</TabsTrigger>
            <TabsTrigger value="campaign" data-testid="pivot-dim-campaign">By Campaign</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full mptg-table text-sm">
          <thead>
            <tr>
              <th>{dimension === "branch" ? "Branch" : dimension === "client" ? "Client" : "Campaign · Client"}</th>
              <th className="text-right">Campaigns</th>
              {dimension !== "campaign" && <th className="text-right">Clients</th>}
              <th className="text-right">Outstanding</th>
              <th className="text-right">Invoiced YTD</th>
              <th>KPI RAG (MTD)</th>
              <th className="text-right">NPS</th>
              <th className="text-right">CSAT</th>
              <th className="text-right">Open actions</th>
              <th className="text-right">Last MBR (d)</th>
              <th className="text-right">Contract (d)</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="py-6 text-center text-slate-400">Loading…</td></tr>
            ) : data.rows.length === 0 ? (
              <tr><td colSpan={11} className="py-6 text-center text-slate-400">Nothing in scope yet.</td></tr>
            ) : data.rows.map((r) => (
              <tr key={r.key} data-testid={`pivot-row-${r.key}`}>
                <td className="font-semibold">{r.label}</td>
                <td className="text-right tabular-nums">{r.campaign_count}</td>
                {dimension !== "campaign" && <td className="text-right tabular-nums">{r.client_count}</td>}
                <td className={`text-right tabular-nums font-semibold ${r.outstanding > 0 ? "text-[#C0392B]" : "text-slate-400"}`}>
                  {inrCompact(r.outstanding)}
                </td>
                <td className="text-right tabular-nums text-slate-700">{inrCompact(r.invoiced_ytd)}</td>
                <td>
                  <div className="flex items-center gap-1.5">
                    <span className="badge-red rounded px-1.5 py-0.5 text-[11px] font-semibold">{r.kpi_red || 0}</span>
                    <span className="badge-amber rounded px-1.5 py-0.5 text-[11px] font-semibold">{r.kpi_amber || 0}</span>
                    <span className="badge-green rounded px-1.5 py-0.5 text-[11px] font-semibold">{r.kpi_green || 0}</span>
                    <span className="badge-slate rounded px-1.5 py-0.5 text-[11px] font-semibold">{r.kpi_no_data || 0}</span>
                  </div>
                </td>
                <td className="text-right tabular-nums">{r.latest_nps ?? "—"}</td>
                <td className="text-right tabular-nums">{r.latest_csat ?? "—"}</td>
                <td className="text-right tabular-nums">{r.open_actions || 0}</td>
                <td className="text-right tabular-nums text-slate-500">
                  {r.last_mbr_days != null ? `${r.last_mbr_days}d` : "—"}
                </td>
                <td className={`text-right tabular-nums ${r.contract_days_left != null && r.contract_days_left <= 30 ? "text-[#C0392B] font-semibold" : "text-slate-500"}`}>
                  {r.contract_days_left != null ? `${r.contract_days_left}d` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
